import { OperationMS, TabKind } from '../types';
import { buildOperationTabs } from './operationTabs';
import { getRetenuCommande } from './marche/marcheAttribution';

// CTA de navigation vers l'onglet suivant du suivi (acheteur MS).
//
// POURQUOI le calcul vit ici et pas dans le JSX :
//  - La visibilite croise DEUX sources — l'accessibilite de la destination (deja decidee par
//    buildOperationTabs) et la presence d'une action orange sur l'onglet courant (connue du seul
//    composant). Les croiser dans le rendu eparpillerait la regle « un seul CTA orange par
//    onglet » sur quatre composants, sans endroit ou la relire d'un coup.
//  - Les libelles sont contractuels (valides client) : les garder groupes evite qu'une
//    reformulation locale passe inapercue.

export interface NextTabCta {
  label: string;
  target: TabKind;
  hidden: boolean;
}

// La destination fait foi : un CTA menant a un onglet grise serait un cul-de-sac. On relit le
// gating officiel plutot que de le redupliquer — il resterait a resynchroniser a chaque evolution.
const tabAccessible = (operation: OperationMS, kind: TabKind): boolean => {
  const tab = buildOperationTabs(operation).find((t) => t.kind === kind);
  return tab !== undefined && !tab.disabled && !tab.inactive;
};

// Consultation -> Questions et reponses.
// `lancementPropose` : la barre porte « Lancer la consultation ». Tant que l'etape reste a faire,
// c'est elle le CTA ; la navigation est le repli une fois la consultation lancee.
export const consultationNextTab = (operation: OperationMS, lancementPropose: boolean): NextTabCta => ({
  label: 'Voir les questions / réponses',
  target: 'questions_reponses',
  hidden: lancementPropose || !tabAccessible(operation, 'questions_reponses'),
});

// Questions et reponses -> Analyse.
// Aucun bloqueur : la barre de la messagerie ne porte que « Envoyer », qui fait avancer la
// discussion, pas le workflow de l'operation.
export const questionsReponsesNextTab = (operation: OperationMS): NextTabCta => ({
  label: 'Voir les offres reçues',
  target: 'analyse',
  hidden: !tabAccessible(operation, 'analyse'),
});

// Analyse -> Notifications et signatures.
// `cloturePropose` : la barre porte « Clôturer l'analyse et préparer le marché ». On se fie a sa
// VISIBILITE et non a son etat actif : meme grise, elle occupe la place du CTA de l'etape.
export const analyseNextTab = (operation: OperationMS, cloturePropose: boolean): NextTabCta => ({
  label: 'Accéder aux notifications et signatures',
  target: 'notifications',
  hidden: cloturePropose || !tabAccessible(operation, 'notifications'),
});

// Notifications et signatures -> Messagerie.
// `signaturePropose` : la barre porte « Signer et notifier ». Le telechargement du marche signe,
// lui, ne bloque pas — il ne fait pas avancer le workflow.
export const notificationsNextTab = (operation: OperationMS, signaturePropose: boolean): NextTabCta => ({
  label: 'Aller dans la messagerie',
  target: 'messagerie',
  hidden: signaturePropose || !tabAccessible(operation, 'messagerie'),
});

// Messagerie -> Evaluation.
// Le retenu est exige EN PLUS de l'onglet actif : l'onglet Evaluation lit l'avis par la commande
// du retenu, sans lui la destination s'ouvrirait sur du vide.
export const messagerieNextTab = (operation: OperationMS): NextTabCta => ({
  label: 'Évaluer le prestataire',
  target: 'evaluation',
  hidden: !tabAccessible(operation, 'evaluation') || !getRetenuCommande(operation)?.id,
});
